import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { LucideIcon, Search as SearchIcon, Plus } from "lucide-react";
import { Button } from "@headlessui/react";
import { SelectField } from "components/common/CustomSelect";
import RequiredMark from 'components/common/RequiredMark';


export interface SearchCreateOption {
    uuid: string;
    name: string;
    /** secondary line under the name, e.g. "Technology \u00b7 500-1000 employees" */
    subtitle?: string;
    imageUrl: string | null;
}

export interface SearchCreateField {
    /** key used in the object passed to onCreate */
    name: string;
    label: string;
    type?: "text" | "select";
    placeholder?: string;
    options?: { value: string; label: string }[];
}

/**
 * Where the "create new" form is presented once the user clicks the create
 * button:
 * - "dropdown" (default): form replaces the results list inside the same
 *   floating panel, exactly like the original component.
 * - "modal": the results dropdown closes and a parent-supplied modal is
 *   rendered via `renderCreateModal` (e.g. the full <CompaniesModal />), so
 *   rich domain-specific create forms can be reused instead of the generic
 *   field grid.
 * - "inline": form renders in normal document flow directly beneath the
 *   input (not floating); the results dropdown closes. Useful when the
 *   surrounding layout (e.g. a form grid) should reflow around the fields,
 *   as in the "Company Name / Industry / Add / Cancel" row.
 * - "external": this component never renders the create form itself. It
 *   only closes the dropdown and calls `onRequestCreate` so a parent can
 *   render the form wherever it needs to in its own layout (e.g. as a
 *   full-width row spanning a 2-column grid, which a nested div here
 *   couldn't do). Use this together with `<EntityCreateForm />` below.
 */
export type CreateDisplayMode = "dropdown" | "modal" | "inline" | "external";

interface SearchCreateSelectProps<T extends SearchCreateOption> {
    label?: string;
    placeholder?: string;
    /** heading above the results list, e.g. "Existing Companies" */
    listHeading?: string;
    emptyLabel?: string;
    /** label on the button that opens the inline create form */
    createButtonLabel?: string;
    /** heading shown above the create form when createDisplay is "modal" */
    createModalTitle?: string;
    /** icon shown next to each option in the list, defaults to a generic search glyph */
    icon?: LucideIcon;
    value: string;
    options: T[];
    isLoading?: boolean;
    error?: string | false;
    required?: boolean;
    /** debounce delay in ms before onSearchChange fires, default 300 */
    debounceMs?: number;
    /** fields rendered in the inline "create new" form (unused when
     *  createDisplay is "modal", where `renderCreateModal` owns the form) */
    createFields?: SearchCreateField[];
    /** where the create form is presented, default "dropdown" */
    createDisplay?: CreateDisplayMode;
    /** only used when createDisplay="modal": renders the create modal so a
     *  full domain-specific dialog (e.g. <CompaniesModal />) can be reused */
    renderCreateModal?: (args: {
        open: boolean;
        onClose: () => void;
        prefillTerm: string;
    }) => React.ReactNode;
    /** show a trailing "+ Add" affordance on each option row */
    showOptionAddAction?: boolean;
    /** hide the "create new" trigger entirely, turning this into a pure
     *  search-and-select control (no inline/modal/external create flow) */
    hideCreateButton?: boolean;
    /** custom node rendered inside the results area while `isLoading` is true,
     *  e.g. a shimmer list; falls back to a plain "Searching..." label */
    loadingContent?: React.ReactNode;
    /** fully override each result row (e.g. reuse a shared <SuggestedMember />);
     *  receives the option and a `select` callback to run the pick. Falls back
     *  to the default icon + name/subtitle row when not provided */
    renderOption?: (option: T, select: () => void) => React.ReactNode;
    /** clear the input after picking an option and keep the dropdown open,
     *  for multi-add flows (instead of filling the input with the selection) */
    clearOnSelect?: boolean;
    /** true while another page of `options` is on its way in (scroll pagination) */
    isFetchingMore?: boolean;
    /** true when the API has more pages left for the current search term */
    hasMore?: boolean;
    /** fired when the results list is scrolled near its end; pair with
     *  `hasMore` / `isFetchingMore` to page through a long option list */
    onLoadMore?: () => void;
    /** fired (debounced) as the user types, use this to trigger your API call */
    onSearchChange: (term: string) => void;
    /** fired immediately when the user picks an existing option */
    onSelectOption: (option: T) => void;
    /** fired when the user submits the inline create form (unused when
     *  createDisplay is "modal") */
    onCreate?: (data: Record<string, string>) => Promise<T | void>;
    /** only used when createDisplay="external": fired when the user clicks
     *  the create button, with the current search term as a prefill hint */
    onRequestCreate?: (prefillTerm: string) => void;
    /** only used when createDisplay="external": pass true while the
     *  parent-rendered form is open, so this component hides its own
     *  "create new" trigger button in the meantime */
    isExternalCreateFormOpen?: boolean;
    disabled?: boolean;
    dropdownPosition?: "floating" | "static";

}

function SearchCreateSelectInner<T extends SearchCreateOption>({
    label,
    placeholder = "Search or create...",
    listHeading = "Existing Options",
    emptyLabel = "No results found",
    createButtonLabel = "Create New",
    icon: Icon = SearchIcon,
    value,
    options,
    isLoading,
    error,
    required,
    debounceMs = 300,
    createFields = [],
    createDisplay = "dropdown",
    renderCreateModal,
    showOptionAddAction = false,
    hideCreateButton = false,
    loadingContent,
    renderOption,
    clearOnSelect = false,
    isFetchingMore = false,
    hasMore = false,
    onLoadMore,
    onSearchChange,
    onSelectOption,
    onCreate,
    onRequestCreate,
    isExternalCreateFormOpen = false,
    dropdownPosition = "floating",
    disabled,
}: SearchCreateSelectProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value ?? "");
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [isCreating, setIsCreating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // debounce the outbound API-triggering callback; the input itself stays
    // instant/uncontrolled-feeling via local `searchTerm` state
    const debouncedSearchChange = useDebouncedCallback((term: string) => {
        onSearchChange(term);
    }, debounceMs);

    // keep local input text in sync if the controlled value changes from
    // outside (e.g. loading an existing record in an edit form)
    useEffect(() => {
        setSearchTerm(value ?? "");
    }, [value]);

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // "dropdown" mode needs click-outside-to-close for both the results
    // list and the inline form. "modal"/"inline" only need it for the
    // results list, since the form itself isn't a floating panel there.
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const targetPortal = (e.target as HTMLElement | null)?.closest("[data-headlessui-portal]");
            // Ignore clicks inside a *different* headlessui portal (e.g. a nested
            // Listbox rendered from the inline create form) — but not our own
            // ancestor Dialog's portal, since this component may itself sit
            // inside a modal, where such a click is genuinely "outside".
            if (targetPortal && targetPortal !== containerRef.current?.closest("[data-headlessui-portal]")) {
                return;
            }
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                if (createDisplay === "dropdown") {
                    setShowCreateForm(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [createDisplay]);

    // When the field sits near the bottom of a scrollable form/modal the panel
    // opens out of sight, so nudge it in. `block: "nearest"` scrolls the
    // minimum needed and no-ops when the panel is already visible; waiting a
    // frame lets it render (and re-running on isLoading catches the height
    // change when results replace the shimmer).
    useEffect(() => {
        if (!isOpen || disabled) return;
        const frame = requestAnimationFrame(() => {
            panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
        return () => cancelAnimationFrame(frame);
    }, [isOpen, disabled, isLoading, showCreateForm]);

    // scroll-based pagination: pull the next page once the sentinel at the
    // bottom of the results list scrolls into the list's own viewport
    const showResultsList = isOpen && !disabled && !(showCreateForm && createDisplay === "dropdown");

    useEffect(() => {
        // stay detached while a page is in flight, so finishing a fetch
        // re-attaches and re-fires if the sentinel is still in view
        if (!showResultsList || !hasMore || !onLoadMore || isLoading || isFetchingMore) return;
        const node = sentinelRef.current;
        const root = listRef.current;
        if (!node || !root) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            { root, threshold: 0.1 },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [showResultsList, hasMore, onLoadMore, isLoading, isFetchingMore, options.length]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        setShowCreateForm(false);
        debouncedSearchChange(term);
    };

    const handleSelect = (option: T) => {
        // multi-add flows reset the input and keep the dropdown open so the
        // user can keep adding; single-select fills the input and closes
        setSearchTerm(clearOnSelect ? "" : option.name);
        onSelectOption(option);
        if (!clearOnSelect) {
            setIsOpen(false);
            setShowCreateForm(false);
        }
    };

    const firstTextField = useMemo(
        () => createFields.find((f) => (f.type ?? "text") === "text"),
        [createFields],
    );

    const openCreateForm = () => {
        setIsOpen(false);
        if (createDisplay === "external") {
            // this component doesn't own the form in external mode; the
            // parent decides where/how to render it
            onRequestCreate?.(searchTerm);
            return;
        }
        const initial: Record<string, string> = {};
        createFields.forEach((f) => {
            initial[f.name] = f.name === firstTextField?.name ? searchTerm : "";
        });
        setFormData(initial);
        setShowCreateForm(true);
    };

    const closeCreateForm = () => setShowCreateForm(false);

    const canSubmit = firstTextField ? !!formData[firstTextField.name]?.trim() : true;

    const handleAdd = async () => {
        if (!canSubmit) return;
        setIsCreating(true);
        try {
            const created = await onCreate?.(formData);
            const fallbackName = firstTextField ? formData[firstTextField.name] : searchTerm;
            setSearchTerm(created ? created.name : fallbackName);
            setIsOpen(false);
            setShowCreateForm(false);
        } finally {
            setIsCreating(false);
        }
    };

    const renderFormFields = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {createFields.map((field) => (
                <div key={field.name}>
                    <label className="text-xs text-neutral-medium block mb-1">{field.label}</label>
                    {(field.type ?? "text") === "select" ? (
                        <SelectField
                            name={field.name}
                            value={formData[field.name] ?? ""}
                            onChange={(val) => setFormData((prev) => ({ ...prev, [field.name]: val }))}
                            options={field.options ?? []}
                            placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`}
                            darkBg
                            className={{ input: "px-3 h-[38px]", value: "text-sm" }}
                        />
                    ) : (
                        <input
                            type="text"
                            value={formData[field.name] ?? ""}
                            onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                            placeholder={field.placeholder ?? field.label}
                            className="w-full bg-primary-light border border-primary-subtle rounded-lg px-3 py-2 text-sm placeholder-neutral-medium text-neutral focus:outline-none focus:border-blue-500"
                        />
                    )}
                </div>
            ))}
        </div>
    );

    const renderFormActions = (cancelLabel = "Cancel") => (
        <div className="flex justify-end gap-2">
            <Button
                onClick={closeCreateForm}
                className="py-2 px-4 rounded-lg border border-primary-subtle text-sm text-neutral hover:bg-primary-light transition-colors"
            >
                {cancelLabel}
            </Button>
            <Button
                disabled={!canSubmit || isCreating}
                onClick={handleAdd}
                className="py-2 px-4 rounded-lg bg-indigo-500 text-white text-sm font-medium disabled:opacity-50 hover:bg-indigo-600 transition-colors"
            >
                {isCreating ? "Adding..." : "Add"}
            </Button>
        </div>
    );

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="text-sm text-neutral-faded font-medium block mb-1.5">
                    {label}
                    {required && <RequiredMark />}
                </label>
            )}

            <input
                type="text"
                value={searchTerm}
                disabled={disabled}
                onFocus={() => setIsOpen(true)}
                onChange={handleInputChange}
                placeholder={placeholder}
                className={`w-full bg-primary-light border rounded-lg px-4 h-[46px]  text-sm placeholder:font-normal placeholder:text-base placeholder:text-[#374151] text-neutral focus:outline-none transition-colors disabled:opacity-50 ${error ? "border-red-500" : "border-primary-subtle"
                    }`}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

            {/* Results dropdown: shown whenever open, regardless of createDisplay.
                In "dropdown" mode it also hosts the create form once toggled. */}
            {isOpen && !disabled && (
              <div
                    ref={panelRef}
                    className={`${
                    dropdownPosition === "static" ? "relative" : "absolute"
                    } z-20 mt-2  w-full bg-primary-variant border border-primary-subtle rounded-lg shadow-lg overflow-hidden`}
                >
                    {!(showCreateForm && createDisplay === "dropdown") ? (
                        <>
                            <p className="px-4 pt-3 pb-2 text-xs font-semibold text-neutral-medium uppercase tracking-wide">
                                {listHeading}
                            </p>
                            <div className="max-h-40 overflow-y-auto" ref={listRef}>
                                {isLoading &&
                                    (loadingContent ? (
                                        <div className="p-2">{loadingContent}</div>
                                    ) : (
                                        <p className="px-4 py-3 text-sm text-neutral-medium">Searching...</p>
                                    ))}
                                {!isLoading && options.length === 0 && (
                                    <p className="px-4 py-3 text-sm text-neutral-medium">{emptyLabel}</p>
                                )}
                                {!isLoading &&
                                    options.map((option) =>
                                        renderOption ? (
                                            <React.Fragment key={option.uuid}>
                                                {renderOption(option, () => handleSelect(option))}
                                            </React.Fragment>
                                        ) : (
                                            <button
                                                type="button"
                                                key={option.uuid}
                                                onClick={() => handleSelect(option)}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary-light text-left transition-colors"
                                            >
                                                <span className="w-8 h-8 rounded-md bg-primary-light flex items-center justify-center shrink-0">
                                                    <Icon className="w-4 h-4 text-neutral-medium" />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium text-neutral truncate capitalize">
                                                        {option.name}
                                                    </span>
                                                    {option.subtitle && (
                                                        <span className="block text-xs text-neutral-medium truncate capitalize">
                                                            {option.subtitle}
                                                        </span>
                                                    )}
                                                </span>
                                                {showOptionAddAction && (
                                                    <span className="ml-auto flex items-center gap-1 shrink-0 text-xs font-medium text-neutral">
                                                        <Plus className="w-3.5 h-3.5" />
                                                        Add
                                                    </span>
                                                )}
                                            </button>
                                        ),
                                    )}
                                {!isLoading && isFetchingMore && (
                                    <p className="px-4 py-2.5 text-xs text-neutral-medium">Loading more...</p>
                                )}
                                {/* invisible sentinel that triggers onLoadMore when scrolled into view */}
                                {!isLoading && hasMore && (
                                    <div ref={sentinelRef} className="h-1 w-full" />
                                )}
                            </div>
                            {!hideCreateButton && (
                                <div className="border-t border-primary-subtle p-3">
                                    <button
                                        type="button"
                                        onClick={openCreateForm}
                                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-primary-subtle text-sm font-medium text-indigo-400 hover:bg-primary-light transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        {createButtonLabel}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="p-4 flex flex-col gap-4">
                            {renderFormFields()}
                            {renderFormActions()}
                        </div>
                    )}
                </div>
            )}

            {/* Dropdown mode has no separate create button once results are
                hidden (handled above). Inline/modal modes need their own
                trigger since the create form isn't nested inside the results
                panel. */}
            {/* {!isOpen &&
                !disabled &&
                !showCreateForm &&
                createDisplay !== "dropdown" &&
                !(createDisplay === "external" && isExternalCreateFormOpen) && (
                    <button
                        type="button"
                        onClick={openCreateForm}
                        className="mt-2 flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        {createButtonLabel}
                    </button>
                )} */}

            {/* Inline mode: form renders in normal flow beneath the input,
                pushing surrounding layout down instead of floating over it. */}
            {createDisplay === "inline" && showCreateForm && (
                <div className="mt-3 p-4 flex flex-col gap-4 bg-primary-variant border border-primary-subtle rounded-lg">
                    {renderFormFields()}
                    {renderFormActions()}
                </div>
            )}

            {/* Modal mode: parent-supplied modal (e.g. <CompaniesModal />)
                owns the create form; we only manage the open/close state and
                hand over the current search term as a prefill hint. */}
            {createDisplay === "modal" &&
                renderCreateModal?.({
                    open: showCreateForm,
                    onClose: closeCreateForm,
                    prefillTerm: searchTerm,
                })}
        </div>
    );
}

// cast to a plain FC type export so generics still work at call sites without
// TS complaining about calling a generic function component via JSX
const SearchCreateSelect = SearchCreateSelectInner as <T extends SearchCreateOption>(
    props: SearchCreateSelectProps<T>,
) => React.ReactElement;

export default SearchCreateSelect;

// ---------------------------------------------------------------------------
// EntityCreateForm: standalone create-fields row, meant to be placed by a
// parent wherever it needs to live in its own layout (e.g. a full-width row
// under a 2-column grid) — used together with createDisplay="external" above.
// ---------------------------------------------------------------------------

export interface EntityCreateFormProps<T> {
    fields: SearchCreateField[];
    initialValues?: Record<string, string>;
    onCreate: (data: Record<string, string>) => Promise<T | void>;
    onCreated?: (created: T) => void;
    onCancel: () => void;
    submitLabel?: string;
    cancelLabel?: string;
    className?: string;
}

export function EntityCreateForm<T>({
    fields,
    initialValues,
    onCreate,
    onCreated,
    onCancel,
    submitLabel = "Add",
    cancelLabel = "Cancel",
    className = "",
}: EntityCreateFormProps<T>) {
    const firstTextField = useMemo(
        () => fields.find((f) => (f.type ?? "text") === "text"),
        [fields],
    );

    const [formData, setFormData] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        fields.forEach((f) => {
            initial[f.name] = initialValues?.[f.name] ?? "";
        });
        return initial;
    });
    const [isCreating, setIsCreating] = useState(false);

    const canSubmit = firstTextField ? !!formData[firstTextField.name]?.trim() : true;

    const handleAdd = async () => {
        if (!canSubmit) return;
        setIsCreating(true);
        try {
            const created = await onCreate(formData);
            if (created) onCreated?.(created);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div
            className={`flex flex-col md:flex-row md:items-end gap-3 p-4  border border-primary-subtle rounded-lg ${className}`}
        >
            {fields.map((field) => (
                <div key={field.name} className={(field.type ?? "text") === "text" ? "flex-1 min-w-0" : "md:w-48"}>
                    <label className="text-xs text-neutral-medium block mb-1">{field.label}</label>
                    {(field.type ?? "text") === "select" ? (
                        <SelectField
                            name={field.name}
                            value={formData[field.name] ?? ""}
                            onChange={(val) => setFormData((prev) => ({ ...prev, [field.name]: val }))}
                            options={field.options ?? []}
                            placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`}
                            darkBg
                            className={{ input: "px-3 h-[38px]", value: "text-sm" }}
                        />
                    ) : (
                        <input
                            type="text"
                            value={formData[field.name] ?? ""}
                            onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                            placeholder={field.placeholder ?? field.label}
                            className="w-full bg-primary-light border border-primary-subtle rounded-lg px-3 py-2 text-sm placeholder-neutral-medium text-neutral focus:outline-none focus:border-blue-500"
                        />
                    )}
                </div>
            ))}
            <div className="flex gap-2 shrink-0">
                <Button
                    onClick={onCancel}
                    className="py-2 px-4 rounded-lg border border-primary-subtle text-sm text-neutral hover:bg-primary-light transition-colors"
                >
                    {cancelLabel}
                </Button>
                <Button
                    disabled={!canSubmit || isCreating}
                    onClick={handleAdd}
                    className="py-2 px-4 rounded-lg bg-indigo-500 text-white text-sm font-medium disabled:opacity-50 hover:bg-indigo-600 transition-colors whitespace-nowrap"
                >
                    {isCreating ? "Adding..." : submitLabel}
                </Button>
            </div>
        </div>
    );
}